import React, { useEffect, useMemo } from 'react';
import { uniqBy } from 'lodash';
import { useSubscription } from '@apollo/client';
import { useInstallationContext } from '../../../../context/installation';
import { ChannelTypeInternal, IntegrationType } from '../../../../data-access/gql-types/graphql';
import {
  ON_DEVICE_FIRMWARE_UPDATED,
  ON_RECOVERY_DEVICE_STATUS_RECEIVED_PAYLOAD,
} from '../../../../data-access/subscriptions/lavva-devices';
import { useDevicesAndChannelsContext } from '../../context/devices-and-channels';
import { ChannelType, DeviceType } from '../../types';
import { ChannelSubProps, DeviceSubProps } from '../../types/subscriptions';
import { useBinarySensorSubscriptions } from '../../utils/channels/subscriptions/binary-sensor/subscriptions';
import { useBlindSubscriptions } from '../../utils/channels/subscriptions/blind/subscriptions';
import { useUpdateCommonLavvaState } from '../../utils/channels/subscriptions/common-lavva-state';
import { useGateSubscriptions } from '../../utils/channels/subscriptions/gate/subscriptions';
import { useLightSubscriptions } from '../../utils/channels/subscriptions/light/subscriptions';
import { useSensorSubscriptions } from '../../utils/channels/subscriptions/sensor/subscriptions';
import { useSwitchSubscriptions } from '../../utils/channels/subscriptions/switch/subscriptions';

const ChannelSub: React.FC<ChannelSubProps> = ({ channel }) => {
  switch (channel.data.type) {
    case ChannelTypeInternal.Switch:
      useSwitchSubscriptions({ channelId: channel.id });
      break;
    case ChannelTypeInternal.Blind:
      useBlindSubscriptions({ channelId: channel.id });
      break;
    case ChannelTypeInternal.Light:
      useLightSubscriptions({ channelId: channel.id });
      break;
    case ChannelTypeInternal.Gate:
      useGateSubscriptions({ channelId: channel.id });
      break;
    case ChannelTypeInternal.Sensor:
      useSensorSubscriptions({ channelId: channel.id });
      break;
    case ChannelTypeInternal.BinarySensor:
      useBinarySensorSubscriptions({ channelId: channel.id });
      break;
  }

  return null;
};

const DeviceSub: React.FC<DeviceSubProps> = ({ device }) => {
  const { onDeviceFirmwareUpdated, onRecoveryFirmwareUpdated } = useUpdateCommonLavvaState();
  const { skipLavvaFetch } = useInstallationContext();

  const { data: firmwareUpdated } = useSubscription(ON_DEVICE_FIRMWARE_UPDATED, {
    variables: { deviceId: device?.id },
    skip: skipLavvaFetch,
  });

  const { data: recoveryDeviceStatusReceivedPayload } = useSubscription(ON_RECOVERY_DEVICE_STATUS_RECEIVED_PAYLOAD, {
    variables: { deviceId: device?.id },
    skip: skipLavvaFetch,
  });

  useEffect(() => {
    if (firmwareUpdated) onDeviceFirmwareUpdated(firmwareUpdated.onDeviceFirmwareUpdated);
  }, [firmwareUpdated]);

  useEffect(() => {
    if (recoveryDeviceStatusReceivedPayload) {
      onRecoveryFirmwareUpdated(recoveryDeviceStatusReceivedPayload.onRecoveryDeviceStatusRecievedPayload);
    }
  }, [recoveryDeviceStatusReceivedPayload]);

  return null;
};

export const ChannelSubscriptions: React.FC = () => {
  const { channelList, deviceList } = useDevicesAndChannelsContext();
  const { integrationType } = useInstallationContext();

  const channelSubscriptions = useMemo(() => {
    if (channelList.length && integrationType === IntegrationType.Lavva) {
      return uniqBy(channelList, 'id').map((channel: ChannelType) => <ChannelSub key={channel.id} channel={channel} />);
    }
  }, [channelList, integrationType]);

  const deviceSubscriptions = useMemo(() => {
    if (deviceList.length && integrationType === IntegrationType.Lavva) {
      return uniqBy(deviceList, 'id').map((device: DeviceType) => <DeviceSub key={device.id} device={device} />);
    }
  }, [deviceList, integrationType]);

  return (
    <>
      {channelSubscriptions}
      {deviceSubscriptions}
    </>
  );
};
